import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import { VariableSizeList as VList } from 'react-window';
import {
  Box, Typography, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Chip,
} from '@mui/material';
import { LogEntry, consistentSpinnerSx, LEVEL_COLORS } from '../types';
import { fmtLogTimestamp } from '../utils';
import { LogRow } from './LogRow';

interface VirtualizedLogListProps {
  entries: LogEntry[];
  expandedRows: Set<number>;
  onToggleRow: (id: number) => void;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  totalLines: number;
  infiniteScrollEnabled: boolean;
  isCompact: boolean;
  highlightTerm: string;
}

const COLLAPSED_ROW_HEIGHT = 32;
const EXPANDED_ESTIMATE = 120;

interface RowData {
  entries: LogEntry[];
  expandedRows: Set<number>;
  isCompact: boolean;
  highlightTerm: string;
  onToggle: (id: number) => void;
  onMeasure: (entryId: number, height: number) => void;
  loadingMore: boolean;
  totalLines: number;
}

const RowRenderer = React.memo(
  ({ index, style, data }: { index: number; style: React.CSSProperties; data: RowData }) => {
    const { entries, expandedRows, isCompact, highlightTerm, onToggle, onMeasure, loadingMore, totalLines } = data;
    const measureRef = useRef<HTMLDivElement>(null);

    const entry = index < entries.length ? entries[index] : null;
    const isExpanded = entry ? expandedRows.has(entry.id) : false;

    useEffect(() => {
      if (measureRef.current && entry && isExpanded && !isCompact) {
        const h = measureRef.current.getBoundingClientRect().height;
        if (h > 0) onMeasure(entry.id, h);
      }
    });

    if (index >= entries.length) {
      if (loadingMore) {
        return (
          <div style={style}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={18} variant="determinate" value={30} color="secondary" sx={consistentSpinnerSx} />
              <Typography variant="caption" color="text.secondary">Loading more logs...</Typography>
            </Box>
          </div>
        );
      }
      return (
        <div style={style}>
          <Box sx={{ textAlign: 'center', py: 1.5 }}>
            <Typography variant="caption" color="text.disabled">
              End of results &mdash; {totalLines} total log lines loaded
            </Typography>
          </Box>
        </div>
      );
    }

    return (
      <div style={style}>
        <div ref={isExpanded && !isCompact ? measureRef : undefined}>
          <LogRow
            entry={entry!}
            isExpanded={isExpanded}
            isOdd={index % 2 !== 0}
            isCompact={isCompact}
            highlightTerm={highlightTerm}
            onToggle={onToggle}
          />
        </div>
      </div>
    );
  },
  (prev, next) => {
    if (prev.index !== next.index) return false;
    if (prev.style.top !== next.style.top || prev.style.height !== next.style.height) return false;
    const pData = prev.data;
    const nData = next.data;
    if (pData.entries !== nData.entries) return false;
    if (pData.expandedRows !== nData.expandedRows) return false;
    if (pData.isCompact !== nData.isCompact) return false;
    if (pData.highlightTerm !== nData.highlightTerm) return false;
    if (pData.loadingMore !== nData.loadingMore) return false;
    if (pData.totalLines !== nData.totalLines) return false;
    return true;
  },
);
RowRenderer.displayName = 'RowRenderer';

export const VirtualizedLogList: React.FC<VirtualizedLogListProps> = React.memo(({
  entries,
  expandedRows,
  onToggleRow,
  hasMore,
  loadingMore,
  onLoadMore,
  totalLines,
  infiniteScrollEnabled,
  isCompact,
  highlightTerm,
}) => {
  const listRef = useRef<VList>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const measuredHeights = useRef<Record<number, number>>({});
  const [dialogEntry, setDialogEntry] = React.useState<LogEntry | null>(null);

  const onMeasure = useCallback((entryId: number, height: number) => {
    const prev = measuredHeights.current[entryId];
    if (prev !== undefined && Math.abs(prev - height) < 2) return;
    measuredHeights.current[entryId] = height;
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, false);
    }
  }, []);

  const getItemSize = useCallback((index: number): number => {
    if (index >= entries.length) return 40;
    if (isCompact) return COLLAPSED_ROW_HEIGHT;
    const entry = entries[index];
    if (expandedRows.has(entry.id)) {
      const cached = measuredHeights.current[entry.id];
      if (cached !== undefined) return cached;
      return EXPANDED_ESTIMATE;
    }
    return COLLAPSED_ROW_HEIGHT;
  }, [entries, expandedRows, isCompact]);

  useEffect(() => {
    Object.keys(measuredHeights.current).forEach((k) => {
      if (!expandedRows.has(Number(k))) {
        delete measuredHeights.current[Number(k)];
      }
    });
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [expandedRows, entries.length, isCompact]);

  const handleToggle = useCallback((id: number) => {
    if (isCompact) {
      const entry = entries.find(e => e.id === id);
      if (entry) setDialogEntry(entry);
    } else {
      onToggleRow(id);
    }
  }, [onToggleRow, isCompact, entries]);

  const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }: { scrollOffset: number; scrollUpdateWasRequested: boolean }) => {
    if (scrollUpdateWasRequested || !infiniteScrollEnabled) return;
    const outer = outerRef.current;
    if (!outer) return;
    const nearBottom = outer.scrollHeight - scrollOffset - outer.clientHeight < 200;
    if (nearBottom && hasMore && !loadingMore) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore, infiniteScrollEnabled]);

  const itemCount = entries.length + (loadingMore ? 1 : 0) + (!hasMore && entries.length > 0 ? 1 : 0);

  const itemData: RowData = useMemo(() => ({
    entries,
    expandedRows,
    isCompact,
    highlightTerm,
    onToggle: handleToggle,
    onMeasure,
    loadingMore,
    totalLines,
  }), [entries, expandedRows, isCompact, highlightTerm, handleToggle, onMeasure, loadingMore, totalLines]);

  const dialogBorderColor = dialogEntry ? (LEVEL_COLORS[dialogEntry.level] || LEVEL_COLORS.unknown) : undefined;

  return (
    <>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <AutoSizer>
          {(height: number, width: number) => (
            <VList
              ref={listRef}
              outerRef={outerRef}
              height={height}
              width={width}
              itemCount={itemCount}
              itemSize={getItemSize}
              itemData={itemData}
              overscanCount={20}
              onScroll={handleScroll}
            >
              {RowRenderer}
            </VList>
          )}
        </AutoSizer>
      </Box>

      {/* Mobile log detail dialog */}
      <Dialog
        open={!!dialogEntry}
        onClose={() => setDialogEntry(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderTop: dialogBorderColor ? `4px solid ${dialogBorderColor}` : undefined },
        }}
      >
        {dialogEntry && (
          <>
            <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary' }}
              >
                {fmtLogTimestamp(dialogEntry.timestamp)}
              </Typography>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  px: 0.75, py: 0.25, borderRadius: 1, minWidth: 42,
                  backgroundColor: dialogBorderColor,
                  color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: 0.3,
                }}
              >
                {dialogEntry.level === 'unknown' ? '???' : dialogEntry.level}
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1.7,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all', userSelect: 'text',
                  mb: Object.keys(dialogEntry.labels).length > 0 ? 2 : 0,
                }}
              >
                {dialogEntry.line}
              </Typography>
              {Object.keys(dialogEntry.labels).length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 700, display: 'block', mb: 0.75, fontSize: '0.72rem' }}
                  >
                    Labels
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {Object.entries(dialogEntry.labels).map(([k, v]) => (
                      <Chip
                        key={k}
                        label={<span><strong>{k}</strong>={v}</span>}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem', fontFamily: 'monospace', borderColor: 'text.disabled' }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialogEntry(null)} variant="outlined" color="secondary" size="small">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
});

VirtualizedLogList.displayName = 'VirtualizedLogList';

function AutoSizer({ children }: { children: (height: number, width: number) => React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = React.useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize((prev) => {
          if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) return prev;
          return { width, height };
        });
      }
    });
    ro.observe(el);

    const rect = el.getBoundingClientRect();
    setSize({ width: rect.width, height: rect.height });

    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      {size.width > 0 && size.height > 0 ? children(size.height, size.width) : null}
    </div>
  );
}
